import {
  shouldAttachApplicationGlobalHeaders,
  type BuildAssetFilterRulesInput,
} from '@terra/shared-asset-processing';

export type ApplicationGlobalHeaders = Record<string, string>;

export function toApplicationGlobalHeaders(
  savedHeaders: ReadonlyArray<{ key: string; value: string }> | undefined,
): ApplicationGlobalHeaders | null {
  const entries = (savedHeaders ?? []).filter(
    ({ key, value }) => key.trim() !== '' && value !== '',
  );

  if (entries.length === 0) {
    return null;
  }

  return Object.fromEntries(entries.map(({ key, value }) => [key, value]));
}

export function withApplicationGlobalHeaders({
  headers,
  url,
  globalHeaders,
  applicationUrl,
  domains,
}: {
  headers: Record<string, string | string[]>;
  url: string;
  globalHeaders: ApplicationGlobalHeaders | null | undefined;
  applicationUrl: string | null;
  domains: BuildAssetFilterRulesInput['domains'];
}): Record<string, string | string[]> {
  const globalHeaderEntries = Object.entries(globalHeaders ?? {});

  if (globalHeaderEntries.length === 0 || !applicationUrl) {
    return headers;
  }

  if (!shouldAttachApplicationGlobalHeaders({ url, applicationUrl, domains })) {
    return headers;
  }

  const requestHeaderNames = new Set(
    Object.keys(headers).map((name) => name.toLowerCase()),
  );
  const missingEntries = globalHeaderEntries
    .map(([name, value]): [string, string] => [name.toLowerCase(), value])
    .filter(([name]) => !requestHeaderNames.has(name));

  return { ...headers, ...Object.fromEntries(missingEntries) };
}

export function appendMissingRawHeaders({
  rawHeaders,
  headers,
}: {
  rawHeaders: readonly string[] | undefined;
  headers: Record<string, string | string[]>;
}): string[] {
  const next = [...(rawHeaders ?? [])];
  const present = new Set<string>();

  for (let index = 0; index < next.length; index += 2) {
    present.add(next[index].toLowerCase());
  }

  for (const [name, value] of Object.entries(headers)) {
    if (present.has(name.toLowerCase())) {
      continue;
    }

    if (Array.isArray(value)) {
      for (const item of value) {
        next.push(name, item);
      }

      continue;
    }

    next.push(name, String(value));
  }

  return next;
}
